// Renders a ```netwatch-findings block: the whole result of a runbook run as
// one card — verdict line, the 7-day same-hour pattern strip (green tick for
// a clean day, a sparkline only where something spiked), every metric that
// moved with the lead (each with its own small chart against it), the checks
// that stayed normal collapsed by group, and the visibility-gaps row.
//
// Every word and number comes from the backend block (docs/runbook-findings-
// block.md); this component owns layout, the fallback ladder and nothing else.
// Shared by the widget (shadow DOM) and the dashboard — hosts load
// `@netwatchai/chat-core/findings.css`.

import { Fragment, useId, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { NwChart } from './NwChart'
import { SeverityIcon } from './Severity'
import { normalizeChartBlock } from './chartSpec'
import {
  clockLabel,
  dayCaption,
  dayKind,
  dayLabel,
  daySpans,
  formatCorrelation,
  groupPill,
  heatCaption,
  heatGrid,
  heatNum,
  heatRamp,
  heatRange,
  laneBox,
  laneDuration,
  laneSpan,
  laneTally,
  meterTone,
  normalizeFindings,
  occurrenceSlots,
  occurrenceHours,
  seenFraction,
  patternKind,
  fillPct,
  barColumn,
  runwayLayout,
  RUNWAY_STOPS,
  RUNWAY_STABLE_X,
  scoreLevel,
  scoreX,
  slatePill,
  slateWord,
  spanLabel,
  spanTip,
  sparklinePath,
  sparklineThresholdY,
  tableShape,
  tallyWords,
  tilePairs,
  windowMark,
  windowRange,
  markTip,
  captionRange,
  type FindingsBlock as FindingsData,
  type FindingsCheck,
  type FindingsDay,
  type FindingsGroup,
  type FindingsMover,
  type FindingsOccurrence,
  type FindingsPattern,
  type FindingsProcesses,
  type FindingsProcessOther,
  type FindingsProcessLedgerRow,
  type FindingsSlate,
  type FindingsTable,
  type FindingsTableRow,
} from './findingsSpec'

export type { FindingsData }

interface FindingsBlockProps {
  data: FindingsData | null | undefined
  raw?: string
  /** The turn is still streaming — an unresolved ref is expected, not broken. */
  busy?: boolean
  /** Host-supplied content rendered inside the card frame, after every
   *  data-driven section (the incident detail's diagnosis reading —
   *  remediation, caveats). The block data itself stays deterministic. */
  footer?: ReactNode
}

const RAW_LIMIT = 2048
const SPARK_W = 72
const SPARK_H = 26

export function FindingsBlock({ data, raw, busy, footer }: FindingsBlockProps) {
  const result = useMemo(() => normalizeFindings(data), [data])

  if (!result.ok) {
    if (result.reason === 'findings not loaded') {
      return busy
        ? <div className="nw-fb nw-fb-skel" aria-busy="true"><div className="nw-fb-skel-bar" /><div className="nw-fb-skel-bar short" /></div>
        : <div className="nw-fb"><div className="nw-fb-note">Findings unavailable — ask me to run the check again.</div></div>
    }
    const text = raw ?? ''
    return (
      <div className="nw-fb">
        <div className="nw-fb-note">Couldn’t show these findings. Ask me to run the check again.</div>
        {text.length > 0 && (
          <details className="nw-fb-rawwrap"><summary>Show data</summary><pre className="nw-fb-raw">{text.slice(0, RAW_LIMIT)}</pre></details>
        )}
      </div>
    )
  }

  const { verdict, checked, caption, leadDetail, pattern, tz, movers, normal, slate, look,
          recurring, processes, problems, next, tables } = result.view
  return (
    <section className={`nw-fb nw-fb-${verdict.key}`} aria-label={verdict.title}>
      <header className="nw-fb-verdict">
        <SeverityIcon level={verdict.level} size={18} title={verdict.title} />
        <div className="nw-fb-verdict-text">
          <div className="nw-fb-verdict-title">
            {verdict.title}
            {verdict.state && (
              <span className={`nw-fb-pill nw-fb-state nw-fb-state-${verdict.state}`} title="how settled the diagnosis is">
                {verdict.state}
              </span>
            )}
          </div>
          {caption && <p className="nw-fb-caption">{caption}</p>}
          {verdict.detail && <p className="nw-fb-verdict-detail">{verdict.detail}</p>}
          {leadDetail && <p className="nw-fb-lead">{leadDetail}</p>}
        </div>
        {checked > 0 && (
          <div className="nw-fb-checked" title="metrics checked across the window">
            <span className="nw-fb-checked-n">{checked}</span>
            <span className="nw-fb-checked-l">checked</span>
          </div>
        )}
      </header>

      {pattern && <Pattern pattern={pattern} />}

      {recurring && <Lines title={recurring.title} subtitle={recurring.subtitle} lines={recurring.lines} />}

      {movers && (
        <div className="nw-fb-sec">
          <h4 className="nw-fb-h">{movers.title}</h4>
          {movers.summary && <p className="nw-fb-p">{movers.summary}</p>}
          {movers.items.map((m) => <Mover key={m.check} mover={m} />)}
        </div>
      )}

      {processes && <Processes section={processes} tz={tz} />}

      {tables.map((t, i) => <Table key={i} table={t} />)}

      {slate && <Slate slate={slate} />}

      {look && (
        <div className="nw-fb-sec nw-fb-look">
          <h4 className="nw-fb-h">{look.title}</h4>
          <p className="nw-fb-p">{look.text}</p>
        </div>
      )}

      {normal && (
        <div className="nw-fb-sec">
          <h4 className="nw-fb-h">{normal.title}</h4>
          {normal.summary && <p className="nw-fb-p">{normal.summary}</p>}
          <div className="nw-fb-groups">
            {normal.groups.map((g) => <Group key={g.key} group={g} windows={Math.max(1, normal.windows?.length ?? 1)} />)}
            {normal.gaps.count > 0 && <Gaps count={normal.gaps.count} labels={normal.gaps.labels} />}
          </div>
        </div>
      )}

      {problems && (
        <div className="nw-fb-sec nw-fb-problems">
          <h4 className="nw-fb-h">{problems.title}</h4>
          <ul className="nw-fb-lines">
            {problems.names.map((n, i) => <li key={i}>{n}</li>)}
            {problems.count > problems.names.length && (
              <li className="nw-fb-lines-more">…and {problems.count - problems.names.length} more</li>
            )}
          </ul>
        </div>
      )}

      {next && (
        <div className="nw-fb-sec nw-fb-next">
          <h4 className="nw-fb-h">{next.title}</h4>
          <ul className="nw-fb-lines">{next.items.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      {footer && <div className="nw-fb-sec nw-fb-footer">{footer}</div>}
    </section>
  )
}

// --- markdown-ish bullet lines from the evidence trail (amendment 20) ---

/** `**bold**` and `` `code` `` — all the collectors emit; the markdown
 *  pipeline stays in chat. Exported for hosts composing footer content. */
export function inlineMarks(text: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /\*\*(.+?)\*\*|`([^`]+)`/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    out.push(m[1] !== undefined ? <b key={out.length}>{m[1]}</b> : <code key={out.length}>{m[2]}</code>)
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Flat "- …" bullets with "  - …" evidence nested under the previous claim. */
function Lines({ title, subtitle, lines }: { title: string; subtitle?: string; lines: string[] }) {
  const items: { text: string; subs: string[] }[] = []
  for (const raw of lines) {
    if (typeof raw !== 'string' || !raw.trim()) continue
    const nested = /^\s+-\s/.test(raw)
    const text = raw.replace(/^\s*-\s*/, '').trim()
    if (nested && items.length > 0) items[items.length - 1].subs.push(text)
    else items.push({ text, subs: [] })
  }
  return (
    <div className="nw-fb-sec">
      <h4 className="nw-fb-h">{title}</h4>
      {subtitle && <p className="nw-fb-sub">{subtitle}</p>}
      <ul className="nw-fb-lines">
        {items.map((it, i) => (
          <li key={i}>
            {inlineMarks(it.text)}
            {it.subs.length > 0 && (
              <ul className="nw-fb-lines">{it.subs.map((s, j) => <li key={j}>{inlineMarks(s)}</li>)}</ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

// --- pattern strip ---

function Pattern({ pattern }: { pattern: FindingsPattern }) {
  return (
    <div className="nw-fb-sec nw-fb-pattern">
      <h4 className="nw-fb-h">{pattern.title}</h4>
      {pattern.subtitle && <p className="nw-fb-p nw-fb-sub">{pattern.subtitle}</p>}
      <div className={`nw-fb-banner nw-fb-banner-${pattern.tone}`}>{pattern.summary}</div>
      {patternKind(pattern) === 'lanes' ? (
        <PatternLanes pattern={pattern} />
      ) : patternKind(pattern) === 'heat' ? (
        <PatternHeat pattern={pattern} />
      ) : (
        <div className="nw-fb-days" role="list">
          {pattern.days.map((d) => <Day key={d.start} day={d} threshold={pattern.threshold} unit={pattern.unit} tz={pattern.tz} />)}
        </div>
      )}
    </div>
  )
}

/** The v2 pattern: one 24h lane per day in the viewer's clock, the asked
 *  window as a tinted column, solid marks for in-window spikes, hollow marks
 *  with a direct time label for spikes at other times. Read down the column
 *  for recurrence, across the lane for everything the window can't see. */
function PatternLanes({ pattern }: { pattern: FindingsPattern }) {
  const days = pattern.days
  const today = days[days.length - 1]
  const win = laneSpan(today.start, today.end, laneBox(today, pattern.tz))
  const tally = laneTally(pattern)
  const words = tallyWords(pattern)
  const derived = pattern.window_kind === 'derived' || pattern.window_kind === 'latest'
  const tag = pattern.window_kind === 'derived' ? 'recurring window'
    : pattern.window_kind === 'latest' ? 'latest outage' : 'this window'
  return (
    <>
      <div className="nw-fb-lanes">
        <div />
        <div className="nw-fb-lanes-axis" aria-hidden="true">
          <span className="nw-fb-lanes-t first" style={{ left: 0 }}>00:00</span>
          <span className="nw-fb-lanes-t mid" style={{ left: '25%' }}>06:00</span>
          <span className="nw-fb-lanes-t mid" style={{ left: '50%' }}>12:00</span>
          <span className="nw-fb-lanes-t mid" style={{ left: '75%' }}>18:00</span>
          <span className="nw-fb-lanes-t last" style={{ left: '100%' }}>24:00</span>
          <span className={`nw-fb-lanes-wintag${win.left > 62 ? ' right' : ''}${derived ? ' derived' : ''}`} style={{ left: `${win.left}%` }}>
            {`${windowRange(today, derived, pattern.tz)} · ${tag}`}
          </span>
        </div>
        <div className="nw-fb-lanes-head" aria-hidden="true">In the window</div>
        {days.map((d) => (
          <Lane key={d.start} day={d} unit={pattern.unit} today={d === today} derived={derived}
                word={pattern.mark_word} cleanWord={pattern.tally_labels?.clean} tz={pattern.tz} />
        ))}
      </div>
      <div className="nw-fb-lanes-tally">
        <span><i className="nw-fb-key" aria-hidden="true" /><b>{tally.in_window}</b> {words.in_window}</span>
        {tally.other > 0 && <span><i className="nw-fb-key nw-fb-key-other" aria-hidden="true" /><b>{tally.other}</b> {words.other}</span>}
        <span><i className="nw-fb-key nw-fb-key-ok" aria-hidden="true" /><b>{tally.clean}</b> {words.clean}</span>
        {tally.no_data > 0 && <span><b>{tally.no_data}</b> {words.no_data}</span>}
        {pattern.tally_note && <span className="nw-fb-lanes-tally-note">{pattern.tally_note}</span>}
      </div>
    </>
  )
}

function Lane({ day, unit, today, derived, word, cleanWord, tz }: {
  day: FindingsDay; unit: string; today: boolean; derived?: boolean; word?: string
  cleanWord?: string; tz?: string | null
}) {
  const box = laneBox(day, tz)
  const win = laneSpan(day.start, day.end, box)
  const mark = windowMark(day)
  const spans = daySpans(day)
  const caption = day.outcome === 'clean' && cleanWord ? cleanWord : dayCaption(day, word || 'spike', tz)
  const dur = laneDuration(day)
  const tip = markTip(day, unit, word || 'spike', tz)
  const capRange = captionRange(day, tz)
  const rowCls = today ? ' nw-fb-lane-today' : ''
  return (
    <>
      <div className={`nw-fb-lane-day${rowCls}`}>
        {dayLabel(day, tz)}
        {today && <small>today</small>}
      </div>
      <div
        className={`nw-fb-lane${rowCls}`}
        role="img"
        aria-label={`${dayLabel(day, tz)}: ${tip ?? caption}${spans.map((sp) => `, ${spanTip(sp, unit, tz)}`).join('')}`}
      >
        {[25, 50, 75].map((p) => <span key={p} className="nw-fb-lane-grid" style={{ left: `${p}%` }} />)}
        <span className={`nw-fb-lane-win${derived ? ' derived' : ''}`} style={{ left: `${win.left}%`, width: `${win.width}%` }} />
        {mark && (() => {
          const m = laneSpan(mark.from, mark.till, box)
          const side = m.left < 14 ? ' tipleft' : m.left + m.width > 86 ? ' tipright' : ''
          return (
            <span
              className={`nw-fb-lane-mark${side}`}
              style={{ left: `${m.left}%`, width: `${m.width}%` }}
              data-tip={tip ?? undefined}
            />
          )
        })()}
        {spans.map((sp) => {
          const m = laneSpan(sp.from, sp.till, box)
          const flip = m.left > 82
          return (
            <Fragment key={sp.from}>
              <span
                className={`nw-fb-lane-mark nw-fb-lane-other${sp.relative ? ' nw-fb-lane-rel' : ''}${m.left < 14 ? ' tipleft' : m.left + m.width > 86 ? ' tipright' : ''}`}
                style={{ left: `${m.left}%`, width: `${m.width}%` }}
                data-tip={spanTip(sp, unit, tz)}
              />
              <span
                className={`nw-fb-lane-markt${sp.relative ? ' nw-fb-lane-markt-rel' : ''}${flip ? ' left' : ''}`}
                style={flip ? { left: `calc(${m.left}% - 5px)` } : { left: `calc(${m.left}% + max(12px, ${m.width}%) + 5px)` }}
              >
                {spanLabel(sp, tz)}
              </span>
            </Fragment>
          )
        })}
      </div>
      <div className={`nw-fb-lane-cap nw-fb-lane-cap-${day.outcome}${rowCls}`}>
        {day.outcome === 'clean' && <SeverityIcon level="ok" size={12} title="clean" />}
        <span>{capRange ? `${caption} ${capRange}` : caption}</span>
        {dur && <span className="nw-fb-lane-dim">· {dur}</span>}
        {day.note && <span className="nw-fb-lane-dim">· {day.note}</span>}
      </div>
    </>
  )
}

function Day({ day, threshold, unit, tz }: {
  day: FindingsDay; threshold: number | null; unit: string; tz?: string | null
}) {
  const kind = dayKind(day)
  const caption = dayCaption(day, 'spike', tz)
  return (
    <div className={`nw-fb-day nw-fb-day-${day.outcome}`} role="listitem" aria-label={`${dayLabel(day, tz)}: ${caption}`}>
      <div className="nw-fb-day-label">{dayLabel(day, tz)}</div>
      <div className="nw-fb-day-body">
        {kind === 'tick' && <SeverityIcon level="ok" size={18} title="clean" />}
        {kind === 'spark' && day.points && (
          <svg className="nw-fb-spark" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} width={SPARK_W} height={SPARK_H} aria-hidden="true">
            {(() => {
              const ty = sparklineThresholdY(day.points, SPARK_H, threshold)
              return ty == null ? null : <line className="nw-fb-spark-thr" x1={0} x2={SPARK_W} y1={ty} y2={ty} />
            })()}
            <path className="nw-fb-spark-line" d={sparklinePath(day.points, SPARK_W, SPARK_H, threshold)} />
          </svg>
        )}
        {kind === 'peak' && (
          <div className="nw-fb-day-peak" title="only hourly trends remain for this day">
            {day.peak != null ? `${day.peak}${unit}` : '—'}
          </div>
        )}
        {kind === 'none' && <div className="nw-fb-day-none">—</div>}
      </div>
      <div className="nw-fb-day-cap">{caption}</div>
    </div>
  )
}

/** The chronic / plateau pattern: every hour of the week as a cell tinted by
 *  its peak's distance over the line, the asked window drawn through it (so
 *  "this hour is not special" is visible before it is read), a per-day
 *  hours-over read, and the week's hourly peaks against the line. */
function PatternHeat({ pattern }: { pattern: FindingsPattern }) {
  const ramp = heatRamp(pattern)
  const rows = heatGrid(pattern, ramp)
  const range = heatRange(pattern, ramp)
  // `span` = the lanes ARE the window (a window too long for a "same hour"
  // draws its own hours instead of a strip). There is no separate window to
  // tint through them, and the last lane is the window's last day, not
  // necessarily today.
  const span = pattern.window_kind === 'span'
  const today = pattern.days[pattern.days.length - 1]
  const win = laneSpan(today.start, today.end, laneBox(today, pattern.tz))
  const unit = pattern.unit
  // The peaks track shows the headroom, not 0–100: from just under the lower
  // of (line, lowest peak) to just over the highest peak.
  const lineV = ramp.line
  const lo0 = range ? Math.min(range.min, lineV ?? range.min) : 0
  const hi0 = range ? Math.max(range.max, lineV ?? range.max) : 1
  const pad = Math.max((hi0 - lo0) * 0.25, 0.5)
  const T0 = lo0 - pad
  const T1 = hi0 + pad
  const tx = (v: number) => `${(((v - T0) / (T1 - T0)) * 100).toFixed(2)}%`
  const lineLabel = lineV != null ? `${heatNum(lineV)}${unit}` : null
  return (
    <>
      <div className="nw-fb-lanes nw-fb-heat">
        <div />
        <div className="nw-fb-lanes-axis" aria-hidden="true">
          <span className="nw-fb-lanes-t first" style={{ left: 0 }}>00:00</span>
          <span className="nw-fb-lanes-t mid" style={{ left: '25%' }}>06:00</span>
          <span className="nw-fb-lanes-t mid" style={{ left: '50%' }}>12:00</span>
          <span className="nw-fb-lanes-t mid" style={{ left: '75%' }}>18:00</span>
          <span className="nw-fb-lanes-t last" style={{ left: '100%' }}>24:00</span>
          {!span && (
            <span className={`nw-fb-lanes-wintag${win.left > 62 ? ' right' : ''}`} style={{ left: `${win.left}%` }}>
              {/* The range, not just the opening minute: the tag is the only
                  thing on this strip that says which window "this window" is
                  (docs/timezones.md, "Say which window"). */}
              {windowRange(today, false, pattern.tz)} · this window
            </span>
          )}
        </div>
        <div className="nw-fb-lanes-head" aria-hidden="true">Hours over the line</div>
        {rows.map((row) => {
          const isToday = !span && row.day === today
          const rowCls = isToday ? ' nw-fb-lane-today' : ''
          const caption = heatCaption(row)
          return (
            <Fragment key={row.day.start}>
              <div className={`nw-fb-lane-day${rowCls}`}>
                {dayLabel(row.day, pattern.tz)}
                {isToday && <small>today</small>}
              </div>
              <div
                className={`nw-fb-lane nw-fb-heat-lane${rowCls}`}
                role="img"
                aria-label={`${dayLabel(row.day, pattern.tz)}: ${caption}${row.peak != null ? `, peak ${heatNum(row.peak)}${unit}` : ''}`}
              >
                <div className="nw-fb-heat-cells">
                  {row.cells.map((c) => (
                    <span
                      key={c.t}
                      className={`nw-fb-heat-cell${c.v == null ? ' nd' : ` o${c.level}`}`}
                      title={`${dayLabel(row.day, pattern.tz)} · ${clockLabel(c.t, pattern.tz)}–${clockLabel(c.t + 3600, pattern.tz)}${c.v == null ? ' · no data' : ` · peak ${heatNum(c.v)}${unit}`}`}
                    />
                  ))}
                </div>
                {!span && <span className="nw-fb-lane-win" style={{ left: `${win.left}%`, width: `${win.width}%` }} />}
              </div>
              <div className={`nw-fb-lane-cap ${row.withData ? 'nw-fb-lane-cap-spike' : 'nw-fb-lane-cap-no_data'}${rowCls}`}>
                <span>{caption}</span>
                {row.peak != null && <span className="nw-fb-lane-dim">· peak {heatNum(row.peak)}{unit}</span>}
              </div>
            </Fragment>
          )
        })}
      </div>
      {range && (
        <div className="nw-fb-lanes-tally">
          <span><b>{range.over}</b> of <b>{range.total}</b> hours over{lineLabel ? ` ${lineLabel}` : ' the line'}</span>
          <span><b>{range.under}</b> hours under it</span>
          <span className="nw-fb-heat-key" aria-label="Key: cell colour by hourly peak">
            <span className="nw-fb-heat-kp"><i className="nw-fb-heat-cell o0" /><em>under{lineLabel ? ` ${lineLabel}` : ''}</em></span>
            <span className="nw-fb-heat-kp"><i className="nw-fb-heat-cell o1" /><em>{heatNum(ramp.steps[0])}–{heatNum(ramp.steps[1])}</em></span>
            <span className="nw-fb-heat-kp"><i className="nw-fb-heat-cell o2" /><em>{heatNum(ramp.steps[1])}–{heatNum(ramp.steps[2])}</em></span>
            <span className="nw-fb-heat-kp"><i className="nw-fb-heat-cell o3" /><em>{heatNum(ramp.steps[2])}{unit}+</em></span>
            <span className="nw-fb-heat-kp"><i className="nw-fb-heat-cell nd" /><em>no data</em></span>
          </span>
        </div>
      )}
      {range && (
        <div className="nw-fb-heat-range">
          <div className="nw-fb-heat-range-l">Hourly peaks</div>
          <div className="nw-fb-heat-track" aria-label={`Hourly peaks ${heatNum(range.min)} to ${heatNum(range.max)}${unit}${lineLabel ? `, line at ${lineLabel}` : ''}`}>
            <span className="nw-fb-heat-tick first" style={{ left: 0 }}>{heatNum(T0)}{unit}</span>
            <span className="nw-fb-heat-tick last" style={{ left: '100%' }}>{heatNum(T1)}{unit}</span>
            <span className="nw-fb-heat-band" style={{ left: tx(range.min), width: `${Math.max(((range.max - range.min) / (T1 - T0)) * 100, 0.6).toFixed(2)}%` }} />
            {lineV != null && (
              <>
                <span className="nw-fb-heat-thr" style={{ left: `calc(${tx(lineV)} - 1px)` }} />
                <span className="nw-fb-heat-thr-l" style={{ left: tx(lineV) }}>line {lineLabel}</span>
              </>
            )}
          </div>
          <div className="nw-fb-heat-range-v">
            <span>{heatNum(range.min)} – {heatNum(range.max)}{unit}</span>
            <span className="nw-fb-lane-dim">
              {range.under === 0 && lineV != null
                ? `lowest peak +${heatNum(range.min - lineV)} over the line`
                : range.under === 0 ? 'never under the line' : `${range.under} hours under the line`}
            </span>
          </div>
        </div>
      )}
    </>
  )
}

// --- movers ---

function Mover({ mover }: { mover: FindingsMover }) {
  const chart = useMemo(() => (mover.chart ? normalizeChartBlock(mover.chart, undefined) : null), [mover.chart])
  return (
    <div className={`nw-fb-mover nw-fb-mover-${mover.kind}`}>
      <div className="nw-fb-mover-row">
        <span className="nw-fb-mover-label">{mover.label}</span>
        <span className="nw-fb-mover-val">
          {mover.value_text}
          {mover.baseline_text && <span className="nw-fb-mover-base"> vs {mover.baseline_text}</span>}
        </span>
        <span className="nw-fb-mover-corr" title="correlation with the lead metric over the window">
          {formatCorrelation(mover.correlation)}
        </span>
        <span className={`nw-fb-pill nw-fb-pill-${mover.kind === 'in_band' ? 'inband' : mover.severity === 'strong' ? 'strong' : 'warn'}`}>
          {mover.kind === 'in_band' ? 'within range' : mover.severity === 'strong' ? 'strong' : 'deviating'}
        </span>
      </div>
      {chart && chart.ok && (
        <div className="nw-fb-mover-chart">
          <NwChart
            spec={chart.spec}
            height={140}
            ariaLabel={chart.spec.title}
            inkVar="--nw-text"
            faintVar="--nw-text-tertiary"
            gridVar="--nw-border"
            thresholdVar="--nw-danger"
            surfaceVar="--nw-bg"
          />
        </div>
      )}
    </div>
  )
}

// --- slate: hypotheses the deterministic checks tested ---

function Slate({ slate }: { slate: FindingsSlate }) {
  return (
    <div className="nw-fb-sec nw-fb-slate">
      <h4 className="nw-fb-h">{slate.title}</h4>
      {slate.summary && <p className="nw-fb-p">{slate.summary}</p>}
      <div className="nw-fb-groups">
        {slate.items.map((it) => (
          <div key={it.check} className={`nw-fb-slate-row nw-fb-slate-${it.outcome}`}>
            {/* The resolved finding leads; the tested hypothesis stays as the
                row's tooltip (provenance). Legacy rows have no finding — the
                hypothesis renders as before. */}
            <span className="nw-fb-slate-hyp" title={it.finding ? `Tested: ${it.hypothesis}` : undefined}>
              {it.finding || it.hypothesis}
            </span>
            <span className={`nw-fb-pill nw-fb-pill-${slatePill(it.outcome)}`}>{slateWord(it.outcome)}</span>
            {it.detail && <span className="nw-fb-slate-detail">{it.detail}</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

// --- processes ---

/** Processes measured across the spike windows (docs/process-recurrence.md):
 *  the snapshot nearest the peak as one line, then name · min–max · which
 *  occurrences (or, on a single-window basis, the share of samples). Blocks
 *  without rows keep the collector's snapshot lines. */
function Processes({ section, tz }: { section: FindingsProcesses; tz?: string | null }) {
  if (!section.rows.length) {
    // No grid to draw, but the out-of-window hours may still have drivers —
    // on a quiet host that sub-list IS the section.
    return section.other
      ? (<div className="nw-fb-sec">
          <h4 className="nw-fb-h">{section.title}</h4>
          {section.peak && <p className="nw-fb-p">At the peak{section.peak.at != null ? ` (${clockLabel(section.peak.at, tz)})` : ''}: {section.peak.text}</p>}
          <OtherSpikes other={section.other} tz={tz} />
          {section.note && <p className="nw-fb-note-small">{section.note}</p>}
        </div>)
      : <Lines title={section.title} lines={section.lines} />
  }
  const multi = section.basis === 'spike_windows'
  const peak = section.peak
  const peakClock = peak ? (peak.at != null ? clockLabel(peak.at, tz) : peak.label) : ''
  return (
    <div className="nw-fb-sec">
      <h4 className="nw-fb-h">{section.title}</h4>
      {peak && <p className="nw-fb-p">At the peak{peakClock ? ` (${peakClock})` : ''}: {peak.text}</p>}
      <table className="nw-fb-checks nw-fb-procs">
        <thead>
          <tr className="nw-fb-checks-head">
            <th scope="col" />
            <th scope="col" className="nw-fb-check-val">min – max</th>
            <th scope="col" className="nw-fb-proc-win">{multi ? 'seen in' : 'samples'}</th>
          </tr>
        </thead>
        <tbody>
          {section.rows.map((r) => (
            <ProcRow key={r.name} row={r} multi={multi} occurrences={section.occurrences} tz={tz} />
          ))}
        </tbody>
      </table>
      {section.other && <OtherSpikes other={section.other} tz={tz} />}
      {section.note && <p className="nw-fb-note-small">{section.note}</p>}
    </div>
  )
}

/** One process row. Where the producer published an occurrence roster, the
 *  count becomes a control over a miniature of the strip above: one slot per
 *  occurrence, in the same order, filled where this process was listed — so
 *  two processes that ran in the same spikes line up down the column before
 *  anything is opened — and opening it names the occurrences and gives the
 *  process' peak inside each. Beta feedback asked for both halves: the
 *  fraction against every occurrence (never the subset that still had
 *  snapshot history), and the times behind it.
 *
 *  `noun` names what an occurrence IS, because the same row draws the ledger
 *  underneath, where the occurrences are the hours outside the window rather
 *  than the spike windows. */
function ProcRow({ row, multi, occurrences, tz, noun = 'spike windows' }: {
  row: FindingsProcessLedgerRow; multi: boolean; occurrences: FindingsOccurrence[]
  tz?: string | null; noun?: string
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const slots = multi ? occurrenceSlots(row, occurrences, tz) : []
  const { n, total } = seenFraction(row, occurrences)
  const share = typeof row.presence === 'number' ? `${Math.round(row.presence * 100)}%` : ''
  return (
    <>
      <tr className={open ? 'nw-fb-proc-open' : undefined}>
        <td className={`nw-fb-check-label nw-fb-proc-name${row.plain ? ' is-plain' : ''}`}>
          {/* The ledger pairs the client-readable name with the measured one;
              the grid above has only what the agent measured. */}
          {row.plain || row.name}
          {row.plain && <em className="nw-fb-proc-tech">{row.name}</em>}
        </td>
        <td className="nw-fb-check-val">{row.range_text}</td>
        <td className="nw-fb-proc-win">
          {slots.length > 0 ? (
            <button
              type="button"
              className="nw-fb-occ-btn"
              aria-expanded={open}
              aria-controls={id}
              aria-label={`Seen in ${n} of the ${total} ${noun} — ${open ? 'hide' : 'show'} the times`}
              title={`Seen in ${n} of the ${total} ${noun} — click for the times`}
              onClick={() => setOpen((v) => !v)}
            >
              <span className="nw-fb-occ-slots" aria-hidden="true">
                {slots.map((s, i) => <i key={i} className={`nw-fb-occ-slot is-${s.state}`} />)}
              </span>
              <span className="nw-fb-occ-n">{n}/{total}</span>
              <span className="nw-fb-occ-caret" aria-hidden="true">▸</span>
            </button>
          ) : (
            <span title={multi ? `${noun} it was listed in` : 'share of samples it was listed in'}>
              {multi ? `${n} of ${total}` : share}
            </span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="nw-fb-proc-detailrow">
          <td colSpan={3}>
            <ul className="nw-fb-occ-list" id={id} role="list">
              {slots.map((s, i) => (
                <li key={i} className={`nw-fb-occ-item is-${s.state}`} role="listitem">
                  <i className={`nw-fb-occ-slot is-${s.state}`} aria-hidden="true" />
                  <span className="nw-fb-occ-day">
                    {s.label}{s.today && <small>today</small>}
                  </span>
                  <span className="nw-fb-occ-clock">{s.clock || '—'}</span>
                  <span className="nw-fb-occ-read">
                    {s.state === 'seen'
                      ? <><em>peak</em> {s.peak}</>
                      : s.state === 'unseen' ? 'not listed' : 'no snapshot history'}
                  </span>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  )
}

/** The out-of-window ledger (docs/process-recurrence.md amendment 4).
 *
 *  The strip above draws hours the asked-about window cannot see; this says
 *  what ran in them. It leads with the backend's composed finding — "A backup
 *  job ran in all four hours outside this window" — then NAMES those hours,
 *  from the same epochs the strip is drawn from: a finding that says "these
 *  four hours" without saying which sends the reader hunting across seven
 *  lanes (beta feedback, 2026-09-10).
 *
 *  Under it, the same table as the grid above — name, min–max, `seen in` —
 *  because the reader has just learned to read that table and this is the
 *  same measurement over a different set of hours. One row per process, and
 *  each row's count opens the hours it ran in with its peak inside each.
 */
function OtherSpikes({ other, tz }: { other: FindingsProcessOther; tz?: string | null }) {
  const hours = occurrenceHours(other.occurrences, tz)
  return (
    <div className="nw-fb-procother">
      <p className="nw-fb-procother-find">{other.title}</p>
      {hours && <p className="nw-fb-procother-when">{hours}</p>}
      {(other.subtitle || other.summary) && (
        <p className="nw-fb-procother-sub">
          {other.subtitle}
          {/* The comparison belongs to the finding, not to the table: left
              under the rows it began with the same words as the last one
              ("Microsoft SQL Server…") and read as a fourth process. */}
          {other.summary && <b>{other.subtitle ? ' ' : ''}{other.summary}</b>}
        </p>
      )}
      {other.rows.length > 0 && (
        <table className="nw-fb-checks nw-fb-procs nw-fb-procother-table">
          <thead>
            <tr className="nw-fb-checks-head">
              <th scope="col" />
              <th scope="col" className="nw-fb-check-val">min – max</th>
              <th scope="col" className="nw-fb-proc-win">seen in</th>
            </tr>
          </thead>
          <tbody>
            {other.rows.map((r) => (
              <ProcRow key={r.name} row={r} multi occurrences={other.occurrences}
                       tz={tz} noun={`hours outside this window`} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// --- titled grids (docs/findings-everywhere.md) ---

/** One producer table: title, column heads, formatted string cells. A row's
 *  `tone` colours its leading cell (muted greys the row); `indent` nests it
 *  under the previous row. Wide grids scroll inside the card, never the page. */
function Table({ table }: { table: FindingsTable }) {
  const shape = tableShape(table)
  return (
    <div className={`nw-fb-sec nw-fb-tablesec nw-fb-shape-${shape}`}>
      <h4 className="nw-fb-h">{table.title}</h4>
      {table.rows.length === 0 ? (
        <p className="nw-fb-p">{table.empty ?? 'Nothing to show.'}</p>
      ) : shape === 'bars' ? (
        <Bars table={table} />
      ) : shape === 'tiles' ? (
        <>
          {table.runway && <Runway dots={table.runway} />}
          <Tiles table={table} />
        </>
      ) : shape === 'score' ? (
        <Score table={table} />
      ) : (
        <div className="nw-fb-tablewrap">
          <table className="nw-fb-checks nw-fb-table">
            <thead>
              <tr className="nw-fb-checks-head">
                {table.columns.map((c) => (
                  <th key={c.key} scope="col" className={c.align === 'right' ? 'nw-fb-cell-right' : 'nw-fb-cell-left'}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i} className={`${r.tone ? `nw-fb-row-${r.tone}` : ''}${r.indent ? ' nw-fb-row-indent' : ''}`.trim() || undefined}>
                  {table.columns.map((c, j) => (
                    <td key={c.key} className={`${c.align === 'right' ? 'nw-fb-cell-right' : 'nw-fb-cell-left'}${j === 0 ? ' nw-fb-cell-lead' : ''}`}>
                      {r[c.key] as string}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {table.note && <p className="nw-fb-note-small">{table.note}</p>}
    </div>
  )
}

// --- shaped tables (docs/findings-everywhere.md, "Shaped tables") ---

/** A 64px meter: the fill, the floor tick, the producer's figure beside it. */
function Meter({ fill, floor, tone, text }: { fill: number; floor: number | null | undefined; tone: 'strong' | 'warn' | 'none'; text: string }) {
  return (
    <span className={`nw-fb-meter nw-fb-meter-${tone}`} title={text}>
      <i style={{ ['--fb-v' as string]: `${fillPct(fill)}%` }}>
        {typeof floor === 'number' && <b style={{ left: `${fillPct(floor)}%` }} aria-hidden="true" />}
      </i>
      <em>{text}</em>
    </span>
  )
}

function Chip({ chip }: { chip: NonNullable<FindingsTableRow['chip']> }) {
  return <span className={`nw-fb-chip${chip.tone ? ` nw-fb-chip-${chip.tone}` : ''}`}>{chip.text}</span>
}

/** Bars: name · bar against the target · figure · the trailing columns. */
function Bars({ table }: { table: FindingsTable }) {
  const lead = table.columns[0]
  const barKey = barColumn(table) ?? table.columns[1].key
  const barCol = table.columns.find((c) => c.key === barKey) ?? table.columns[1]
  const trailing = table.columns.filter((c) => c !== lead && c.key !== barKey)
  return (
    <div className="nw-fb-bars" style={{ ['--fb-trailing' as string]: trailing.length }}>
      <div className="nw-fb-bars-head" aria-hidden="true">
        <span>{lead.label}</span>
        <span>{barCol.label}{typeof table.floor === 'number' ? ` · target ${fillPct(table.floor)}%` : ''}</span>
        <span />
        {trailing.map((c) => <span key={c.key}>{c.label}</span>)}
      </div>
      {table.rows.map((r, i) => {
        const fill = r.fills?.[barKey]
        const tone = meterTone(fill ?? 0, null, r.tone)
        return (
          <div key={i} className={`nw-fb-bar-row${r.tone ? ` nw-fb-row-${r.tone}` : ''}`}>
            <span className="nw-fb-bar-name">{r[lead.key] as string}</span>
            <span className={`nw-fb-bar nw-fb-bar-${tone}`} role="img" aria-label={`${r[lead.key] as string}: ${r[barKey] as string}`}>
              {typeof fill === 'number' && <i style={{ width: `${fillPct(fill)}%` }} />}
              {typeof table.floor === 'number' && <b style={{ left: `${fillPct(table.floor)}%` }} aria-hidden="true" />}
            </span>
            <span className={`nw-fb-bar-pct nw-fb-bar-pct-${tone}`}>{r[barKey] as string}</span>
            {trailing.map((c) => <span key={c.key} className="nw-fb-bar-trail">{r[c.key] as string}</span>)}
          </div>
        )
      })}
    </div>
  )
}

/** Tiles: a two-line row per entry — lead cell, sub and chip, then the
 *  other columns as label/value pairs with a meter wherever there is a fill. */
function Tiles({ table }: { table: FindingsTable }) {
  const lead = table.columns[0]
  return (
    <div className="nw-fb-tiles">
      {table.rows.map((r, i) => (
        <div key={i} className={`nw-fb-tile${r.tone ? ` nw-fb-tile-${r.tone}` : ''}`}>
          <div className="nw-fb-tile-head">
            <span className="nw-fb-tile-name">{r[lead.key] as string}</span>
            {r.sub && <span className="nw-fb-tile-sub">{r.sub}</span>}
            {r.chip && <Chip chip={r.chip} />}
          </div>
          <div className="nw-fb-tile-pairs">
            {tilePairs(table, r).map((p) => (
              <span key={p.key} className="nw-fb-pair">
                <span className="nw-fb-pair-l">{p.label}</span>
                {p.fill !== null
                  ? <Meter fill={p.fill} floor={table.floor} tone={meterTone(p.fill, table.floor, r.tone)} text={p.value || `${fillPct(p.fill)}%`} />
                  : <span className="nw-fb-pair-v">{p.value}</span>}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/** The runway strip above the drive tiles: every drive placed by time to full. */
function Runway({ dots }: { dots: NonNullable<FindingsTable['runway']> }) {
  const layout = runwayLayout(dots)
  return (
    <div className="nw-fb-runway" role="img" aria-label={`Runway: ${layout.dots.map((d) => d.text).join(', ')}${layout.stable.length ? `; stable: ${layout.stable.join(', ')}` : ''}`}>
      {RUNWAY_STOPS.map((s) => (
        <Fragment key={s.days}>
          <span className="nw-fb-rw-tick" style={{ left: `${s.x}%` }} aria-hidden="true" />
          <span className={`nw-fb-rw-t${s.x === 0 ? ' first' : ''}`} style={{ left: `${s.x}%` }}>{s.label}</span>
        </Fragment>
      ))}
      <span className="nw-fb-rw-t last" style={{ left: `${RUNWAY_STABLE_X}%` }}>stable</span>
      {layout.dots.map((d) => (
        <span key={d.label} className={`nw-fb-rw-d${d.tone ? ` nw-fb-rw-${d.tone}` : ''}${d.row ? ' row2' : ''}${d.x > 80 ? ' left' : ''}`} style={{ left: `${d.x}%` }}>
          <em>{d.text}</em><i />
        </span>
      ))}
      {layout.stable.length > 0 && (
        <span className="nw-fb-rw-d nw-fb-rw-stable" style={{ left: `${RUNWAY_STABLE_X}%` }}>
          <em>{layout.stable.join(' · ')}</em><i />
        </span>
      )}
    </div>
  )
}

/** Score: the readout on its grade-banded track, the rows as one segmented
 *  bar with the cells as its key. */
function Score({ table }: { table: FindingsTable }) {
  const score = table.score!
  const lead = table.columns[0]
  const fillKey = barColumn(table)
  const rest = table.columns.filter((c) => c !== lead && c.key !== fillKey)
  const level = scoreLevel(score)
  return (
    <>
      <div className="nw-fb-score">
        <div className="nw-fb-score-n">
          {Number.isInteger(score.value) ? score.value : score.value.toFixed(1)}
          <small>/{score.max}{score.grade ? ` · ${score.grade}` : ''}</small>
        </div>
        <div className="nw-fb-score-track" role="img" aria-label={`${score.value} of ${score.max}`}>
          <i className={`nw-fb-score-fill nw-fb-score-${level}`} style={{ width: `${scoreX(score)}%` }} />
          {score.bands.map((b) => (
            <Fragment key={b}>
              <span className="nw-fb-score-band" style={{ left: `${(b / score.max) * 100}%` }} aria-hidden="true" />
              <span className="nw-fb-score-bl" style={{ left: `${(b / score.max) * 100}%` }}>{b}</span>
            </Fragment>
          ))}
        </div>
      </div>
      {fillKey && (
        <div className="nw-fb-sevbar" aria-hidden="true">
          {table.rows.map((r, i) => {
            const w = fillPct(r.fills?.[fillKey])
            return w > 0 ? <i key={i} className={`nw-fb-seg-${r.tone ?? 'none'}`} style={{ width: `${w}%` }} /> : null
          })}
        </div>
      )}
      <div className="nw-fb-sevkey">
        {table.rows.map((r, i) => (
          <span key={i}>
            <i className={`nw-fb-seg-${r.tone ?? 'none'}`} aria-hidden="true" />
            {r[lead.key] as string}
            {fillKey && <b>{r[fillKey] as string}</b>}
            {rest.map((c) => (r[c.key] ? ` · ${(r[c.key] as string)} ${c.label.toLowerCase()}` : '')).join('')}
          </span>
        ))}
      </div>
    </>
  )
}

// --- normal groups ---

function Group({ group, windows }: { group: FindingsGroup; windows: number }) {
  const [open, setOpen] = useState(false)
  const n = group.count
  // Ranges exist only on blocks built after the spike-window sampling landed;
  // older stored blocks fall back to today's value column. The row is the
  // metric and its min–max only: the baseline / vs-7d / corr cells came out
  // of the card in the incident-investigation refactor (the movers section
  // above already tells the deviation story; the table is the range story).
  const ranged = group.checks.some((c) => c.range_text)
  return (
    <div className={`nw-fb-group${open ? ' open' : ''}`}>
      <button type="button" className="nw-fb-group-row" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="nw-fb-group-caret" aria-hidden="true">▸</span>
        <span className="nw-fb-group-label">{group.label}</span>
        <span className="nw-fb-group-count">{n} metric{n === 1 ? '' : 's'}</span>
        <span className={`nw-fb-pill nw-fb-pill-${groupPill(group.outcome).cls}`}>{groupPill(group.outcome).word}</span>
      </button>
      {open && (
        <table className="nw-fb-checks">
          {ranged && (
            <thead>
              <tr className="nw-fb-checks-head">
                <th scope="col" />
                <th scope="col" className="nw-fb-check-val">min – max in window{windows > 1 ? 's' : ''}</th>
              </tr>
            </thead>
          )}
          <tbody>
            {group.checks.map((c) => (
              <tr key={c.label} className={c.outcome === 'elevated' ? 'nw-fb-check-elevated' : undefined}>
                <td className="nw-fb-check-label">{c.label}{c.outcome === 'elevated' && <span className="nw-fb-check-tag">high, usual</span>}</td>
                <td className="nw-fb-check-val" title={rangeTitle(c, windows)}>
                  {c.range_text || c.value_text}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function rangeTitle(c: FindingsCheck, windows: number): string | undefined {
  if (!c.range_text) return undefined
  const k = c.windows ?? 1
  if (windows > 1 && k < windows) return `min – max across ${k} of the ${windows} windows (raw history missing on the rest); today's value ${c.value_text}`
  return windows > 1 ? `min – max across the ${windows} spike windows; today's value ${c.value_text}` : `min – max inside this window; average ${c.value_text}`
}

function Gaps({ count, labels }: { count: number; labels: string[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`nw-fb-group nw-fb-gaps${open ? ' open' : ''}`}>
      <button type="button" className="nw-fb-group-row" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="nw-fb-group-caret" aria-hidden="true">▸</span>
        <span className="nw-fb-group-label">Not collected</span>
        <span className="nw-fb-group-count">{count} check{count === 1 ? '' : 's'}</span>
        <span className="nw-fb-pill nw-fb-pill-muted">no data</span>
      </button>
      {open && <div className="nw-fb-gaps-list">{labels.join(' · ')}</div>}
    </div>
  )
}
